import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import {
  CanAccess,
  useCanAccess,
  useDataProvider,
  useGetList,
  useGetOne,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { Colega, Sale } from "../types";
import {
  buildColegaPayload,
  filterColegas,
  INDEPENDENT_BROKERAGE,
  toColegaErrorMessage,
  toColegaFormValues,
  validateColegaForm,
  type ColegaFormErrors,
  type ColegaFormValues,
} from "./colegaForm";

/**
 * Almikia Epic E2b (TASK-022): the "Colegas" list (external brokers who
 * refer leads). Reachable by the four commercial roles only (`CanAccess` on
 * `colegas`/`list`; RLS is the real authority, so the phone is never fetched
 * for anyone else). Everyone commercial can search and create; only
 * direccion / lead_manager see "Editar" and "Desactivar" / "Reactivar".
 * Nothing is ever deleted. No rankings or per-advisor counters.
 */
export const ColegaList = () => (
  <CanAccess
    resource="colegas"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <ColegaListContent />
  </CanAccess>
);

ColegaList.path = "/colegas";

const ColegaListContent = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const isMobile = useIsMobile();
  const { canAccess: canEdit } = useCanAccess({
    resource: "colegas",
    action: "edit",
  });
  const { canAccess: canCreate } = useCanAccess({
    resource: "colegas",
    action: "create",
  });

  const { data, isPending, error, refetch } = useGetList<Colega>("colegas", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "full_name", order: "ASC" },
  });
  const [query, setQuery] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<Colega["id"] | null>(null);
  const [toggleTarget, setToggleTarget] = useState<Colega | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const title = translate("crm.colegas.page_title");

  const handleSave = async (values: ColegaFormValues, colega?: Colega) => {
    const payload = buildColegaPayload(values);
    if (colega) {
      await dataProvider.update("colegas", {
        id: colega.id,
        data: payload,
        previousData: colega,
      });
      setEditingId(null);
      notify("crm.colegas.updated_notice");
    } else {
      await dataProvider.create("colegas", { data: payload });
      setIsAdding(false);
      notify("crm.colegas.created_notice");
    }
    refetch();
  };

  const handleToggleConfirm = async () => {
    if (!toggleTarget) return;
    setIsToggling(true);
    try {
      await dataProvider.update("colegas", {
        id: toggleTarget.id,
        data: { active: !toggleTarget.active },
        previousData: toggleTarget,
      });
      notify(
        toggleTarget.active
          ? "crm.colegas.deactivated_notice"
          : "crm.colegas.reactivated_notice",
      );
      refetch();
    } catch (writeError) {
      notify(toColegaErrorMessage(writeError), { type: "error" });
    } finally {
      setIsToggling(false);
      setToggleTarget(null);
    }
  };

  let body;
  if (error) {
    body = <ListErrorState onRetry={() => refetch()} />;
  } else if (isPending || !data) {
    body = <ListCardsSkeleton rows={4} />;
  } else {
    const visible = filterColegas(data, query);
    body = (
      <>
        {isAdding ? (
          <ColegaForm
            onSave={(values) => handleSave(values)}
            onCancel={() => setIsAdding(false)}
          />
        ) : null}
        {visible.length === 0 ? (
          <ListEmptyState
            title={translate(
              data.length === 0 ? "crm.colegas.empty" : "crm.colegas.no_match",
            )}
            description={
              data.length === 0
                ? translate("crm.colegas.empty_description")
                : undefined
            }
            action={
              data.length > 0 ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="min-h-11"
                  onClick={() => setQuery("")}
                >
                  {translate("crm.common.clear_filters")}
                </Button>
              ) : canCreate && !isAdding ? (
                <Button
                  type="button"
                  className="min-h-11"
                  onClick={() => setIsAdding(true)}
                >
                  {translate("crm.colegas.add_action")}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="divide-y rounded-md border">
            {visible.map((colega) =>
              editingId === colega.id ? (
                <div key={colega.id} className="p-3">
                  <ColegaForm
                    colega={colega}
                    onSave={(values) => handleSave(values, colega)}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div
                  key={colega.id}
                  data-testid="colega-row"
                  className="flex flex-wrap items-center gap-3 p-3"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium truncate">
                        {colega.full_name}
                      </span>
                      {!colega.active ? (
                        <StatusPill
                          tone="not-ok"
                          text={translate("crm.colegas.inactive_marker")}
                        />
                      ) : null}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {colega.brokerage === INDEPENDENT_BROKERAGE
                        ? translate("resources.colegas.fields.independent")
                        : colega.brokerage}
                      {" · "}
                      <span>{colega.phone}</span>
                    </p>
                  </div>
                  {canEdit ? (
                    <div className="flex gap-2 shrink-0">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="min-h-11"
                        onClick={() => setEditingId(colega.id)}
                      >
                        <Pencil className="size-4 mr-1" />
                        {translate("crm.colegas.edit_action")}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant={colega.active ? "destructive" : "default"}
                        className="min-h-11"
                        onClick={() => setToggleTarget(colega)}
                      >
                        {colega.active
                          ? translate("crm.colegas.deactivate_action")
                          : translate("crm.colegas.reactivate_action")}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ),
            )}
          </div>
        )}
      </>
    );
  }

  const content = (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {canCreate && !isAdding ? (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11"
              onClick={() => setIsAdding(true)}
            >
              <Plus className="size-4 mr-1" />
              {translate("crm.colegas.add_action")}
            </Button>
          ) : null}
        </div>
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={translate("crm.colegas.search_placeholder")}
          aria-label={translate("crm.colegas.search_placeholder")}
        />
        {body}
      </CardContent>
      <Confirm
        isOpen={toggleTarget != null}
        loading={isToggling}
        title={
          toggleTarget?.active
            ? "crm.colegas.confirm_deactivate_title"
            : "crm.colegas.confirm_reactivate_title"
        }
        content={
          toggleTarget?.active
            ? "crm.colegas.confirm_deactivate_body"
            : "crm.colegas.confirm_reactivate_body"
        }
        confirm={
          toggleTarget?.active
            ? "crm.colegas.deactivate_action"
            : "crm.colegas.reactivate_action"
        }
        confirmColor={toggleTarget?.active ? "warning" : "primary"}
        onConfirm={handleToggleConfirm}
        onClose={() => setToggleTarget(null)}
      />
    </Card>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }
  return (
    <div className="mx-auto max-w-2xl p-4 pb-8">
      <h1 className="text-2xl font-semibold mb-4">{title}</h1>
      {content}
    </div>
  );
};

/** Shows who registered the colega and when; read-only. */
const ColegaAudit = ({ colega }: { colega: Colega }) => {
  const translate = useTranslate();
  const { data: creator } = useGetOne<Sale>(
    "sales",
    { id: colega.created_by as Colega["id"] },
    { enabled: colega.created_by != null },
  );
  const creatorName = creator
    ? `${creator.first_name} ${creator.last_name}`.trim()
    : "";
  const date = colega.created_at
    ? new Date(colega.created_at).toLocaleDateString("es-MX")
    : "";
  return (
    <p className="text-sm text-muted-foreground">
      {translate("resources.colegas.fields.created_by")}: {creatorName || "-"} ·{" "}
      {date}
    </p>
  );
};

/** Shared create/edit form. Validation runs before saving; server rejections show inline. */
export const ColegaForm = ({
  colega,
  onSave,
  onCancel,
}: {
  colega?: Colega;
  onSave: (values: ColegaFormValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const idPrefix = `colega-${colega?.id ?? "new"}`;
  const [values, setValues] = useState<ColegaFormValues>(
    toColegaFormValues(colega),
  );
  const [errors, setErrors] = useState<ColegaFormErrors>({});
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    const found = validateColegaForm(values);
    setErrors(found);
    setSaveError(undefined);
    if (Object.keys(found).length > 0) return;
    setIsSaving(true);
    try {
      await onSave(values);
    } catch (error) {
      setSaveError(toColegaErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  const field = (
    key: "full_name" | "phone" | "brokerage" | "email" | "notes",
    label: string,
    extra: { disabled?: boolean; type?: string } = {},
  ) => (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
      <Input
        id={`${idPrefix}-${key}`}
        type={extra.type}
        value={values[key]}
        disabled={isSaving || extra.disabled}
        aria-invalid={key !== "notes" && errors[key] != null}
        onChange={(event) =>
          setValues({ ...values, [key]: event.target.value })
        }
      />
      {key !== "notes" && errors[key] ? (
        <p className="text-sm text-destructive">{errors[key]}</p>
      ) : null}
    </div>
  );

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3 bg-muted/30">
      {field("full_name", translate("resources.colegas.fields.full_name"))}
      {field("phone", translate("resources.colegas.fields.phone"), {
        type: "tel",
      })}
      <div className="flex items-center gap-2">
        <Checkbox
          id={`${idPrefix}-independent`}
          checked={values.isIndependent}
          disabled={isSaving}
          onCheckedChange={(checked) =>
            setValues({ ...values, isIndependent: checked === true })
          }
        />
        <Label htmlFor={`${idPrefix}-independent`}>
          {translate("resources.colegas.fields.independent")}
        </Label>
      </div>
      {values.isIndependent
        ? null
        : field("brokerage", translate("resources.colegas.fields.brokerage"))}
      {field("email", translate("resources.colegas.fields.email"), {
        type: "email",
      })}
      {field("notes", translate("resources.colegas.fields.notes"))}
      {colega ? <ColegaAudit colega={colega} /> : null}
      {saveError ? (
        <p className="text-sm text-destructive">{saveError}</p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11"
          disabled={isSaving}
          onClick={onCancel}
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
          onClick={handleSave}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
